import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import {
  FileText,
  Users,
  DollarSign,
  Ticket,
  Calendar,
  Package,
  Briefcase,
  ClipboardList,
  Plus,
  Search,
  RefreshCw
} from 'lucide-react';

// Icon mapping for different entity types
const iconMap = {
  leads: FileText,
  clients: Users,
  invoices: DollarSign,
  tickets: Ticket,
  calendar: Calendar,
  inventory: Package,
  jobs: Briefcase,
  tasks: ClipboardList,
  default: FileText
};

/**
 * EmptyState - Displays a helpful message when no data is available
 * @param {string} title - Main heading
 * @param {string} description - Supporting text
 * @param {string} icon - Icon type from iconMap
 * @param {string} actionLabel - Button text for primary action
 * @param {function} onAction - Callback for primary action
 * @param {string} secondaryActionLabel - Button text for secondary action
 * @param {function} onSecondaryAction - Callback for secondary action
 */
export const EmptyState = ({
  title = "No items found",
  description = "Get started by creating your first item.",
  icon = "default",
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className
}) => {
  const IconComponent = iconMap[icon] || iconMap.default;

  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center py-16 px-4 text-center",
        className
      )}
      data-testid="empty-state"
    >
      <div className="rounded-full bg-muted p-4 mb-4">
        <IconComponent className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {description}
      </p>
      <div className="flex gap-3">
        {actionLabel && onAction && (
          <Button onClick={onAction} data-testid="empty-state-action">
            <Plus className="h-4 w-4 mr-2" />
            {actionLabel}
          </Button>
        )}
        {secondaryActionLabel && onSecondaryAction && (
          <Button variant="outline" onClick={onSecondaryAction} data-testid="empty-state-secondary-action">
            {secondaryActionLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * SearchEmptyState - Specialized empty state for search results
 * @param {string} searchTerm - The search term that returned no results
 * @param {function} onClearSearch - Callback to clear search
 */
export const SearchEmptyState = ({
  searchTerm,
  onClearSearch,
  className
}) => {
  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center py-16 px-4 text-center",
        className
      )}
      data-testid="search-empty-state"
    >
      <div className="rounded-full bg-muted p-4 mb-4">
        <Search className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No results found</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {searchTerm ? (
          <>No items match "<span className="font-medium">{searchTerm}</span>". Try adjusting your search or filters.</>
        ) : (
          "No items match your current filters. Try adjusting or clearing them."
        )}
      </p>
      {onClearSearch && (
        <Button variant="outline" onClick={onClearSearch} data-testid="clear-search-button">
          <RefreshCw className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
      )}
    </div>
  );
};

/**
 * FilteredEmptyState - Empty state when filters return no results
 * @param {function} onClearFilters - Callback to clear filters
 */
export const FilteredEmptyState = ({
  onClearFilters,
  className
}) => {
  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center py-12 px-4 text-center",
        className
      )}
      data-testid="filtered-empty-state"
    >
      <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-3 mb-4">
        <Search className="h-6 w-6 text-amber-600 dark:text-amber-400" />
      </div>
      <h3 className="text-base font-semibold mb-1">No matches</h3>
      <p className="text-sm text-muted-foreground max-w-xs mb-4">
        No items match your current filters.
      </p>
      {onClearFilters && (
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          Clear Filters
        </Button>
      )}
    </div>
  );
};

export default {
  EmptyState,
  SearchEmptyState,
  FilteredEmptyState
};
